import { styled } from "@linaria/react";
import * as React from "react";
import { useNavigate } from "react-router";
import { useGet, usePatch, usePost, usePut } from "../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../i18n";
import { browserTimeZones, defaultTimeZone } from "../../lib/time/intl";
import { comparatorBy } from "diagnostics-lib/comparator";
import ModalForm from "@ui/Modal/Form";
import Select from "@ui/Select";
import { useSources } from "../../api/useSources";
import { useDescriptionInput } from "./useDescriptionInput";
import { useNameInput } from "./useNameInput";
import { useIndexedProjects, useProjects } from "../../api/useProjects";
import { useSourceDataInput } from "./useSourceDataInput";
import { useTimeZoneInput } from "./useTimeZoneInput";
import { useMutateBody } from "../../api/useQuery";
import { color, fonts } from "@ui";
import Rollback from "@ui/Rollback";
import InputV2 from "@ui/Input/InputV2";
import { useProjectTypeInput } from "./useProjectTypeInput";
import { useProjectSiteInput } from "./useProjectSiteInput";
import ReactSelect from "@ui/ReactSelect";
import { canUserDo } from "diagnostics-domain/canUserDo";
import { useUser } from "../../api/useUser";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { useTranslations } from "../../api/useTranslations";
import call from "../../api/call";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { toPath } from "../../router/helpers/path";
interface P {
  onRequestClose: () => void;
  projectIds?: string[];
  preselectedSource?: string;
  okButtonName: string;
}

const Title = styled.div`
  color: ${color.carbon900};
  font: ${fonts.body1};
  display: flex;
  align-items: center;
  margin-bottom: -5px;
`;

const mapValueToOptions = ({ name, id }: { name: string; id: string | null }): BaseOption => ({
  value: id,
  label: name,
  color: color.hydrogen,
});

const CreateUpdateProjectsModalForm = ({
  onRequestClose,
  projectIds,
  preselectedSource,
  okButtonName,
}: P) => {
  const createProject = usePost("/projects");
  const updateProjects = usePatch("/projects");
  const updateProject = usePut("/projects/:projectId(uuid)");
  const getSourceCompatibility = useGet("/projects/:projectId(uuid)/source-compatibility");

  const translations = useTranslations();
  const { addToast } = useToasts();
  const sources = useSources();
  const indexedProjects = useIndexedProjects();
  const allProjects = useProjects();
  const currentAllProjects = React.useRef(allProjects);

  const navigate = useNavigate();
  const projects = React.useMemo(
    () =>
      projectIds
        ? projectIds.flatMap(id => (indexedProjects[id] ? [indexedProjects[id]!] : []))
        : [],
    [indexedProjects, projectIds],
  );
  const mutateProjects = useMutateBody("/projects");
  const user = useUser();
  const useCanEditCategory = canUserDo(user, "CATEGORY_EDIT");

  // Input handlers

  const {
    placeholder: namePlaceholder,
    nameValue,
    handleNameChanged,
    isInputNameDirty,
    inputNameDefaultValue,
  } = useNameInput(projects);

  const {
    placeholder: descriptionPlaceholder,
    description: descriptionValue,
    handleDescriptionChanged,
    isDescriptionDirty,
    descriptionDefaultValue,
  } = useDescriptionInput(projects);

  const {
    placeholder: datasourcePlaceHolder,
    sourceValue: sourceIdInput,
    handleSourceValueChanged,
    isSourceValueDirty,
    handleRollbackSources,
  } = useSourceDataInput(projects, preselectedSource);

  const projectTypeInput = useProjectTypeInput(projects);
  const projectSiteInput = useProjectSiteInput(projects);
  const projectNameInitialyEmpty = React.useRef(nameValue === "");
  const projectTypeInitialyEmpty = React.useRef(projectTypeInput.selectedProjectTypes === null);
  const projectSiteInitialyEmpty = React.useRef(projectSiteInput.selectedProjectSites === null);

  const {
    timeZoneValue,
    isTimeZoneValueDirty,
    onTimeZoneChanged: handleTimeZoneChanged,
    timeZonePlaceholder,
    onRollbackTimeZone: handleRollbackTimeZone,
  } = useTimeZoneInput({ projects });
  const [submitted, setSubmitted] = React.useState(false);

  const _action = projects.length > 1 ? "batchUpdate" : projects.length === 0 ? "create" : "update";
  const batchUpdate = _action === "batchUpdate";
  const create = _action === "create";
  const update = _action === "update";
  const [missingSensorTags, setMissingSensorTags] = React.useState<string[] | null>(null); // null = loading

  React.useEffect(() => {
    if (
      !projects.length ||
      !sourceIdInput ||
      create ||
      batchUpdate ||
      sourceIdInput === projects[0]!.sourceId
    ) {
      setMissingSensorTags([]);
      return;
    }

    const currentSourceIdInput = sourceIdInput;
    setMissingSensorTags(null);
    getSourceCompatibility({
      routeParams: { projectId: projects[0]!.id },
      query: { sourceId: sourceIdInput },
    })
      .then(({ body: { missingSensorTags } }) => {
        if (currentSourceIdInput === sourceIdInput) setMissingSensorTags(missingSensorTags);
      })
      .catch(error => {
        if (currentSourceIdInput === sourceIdInput) setMissingSensorTags([]);
        throw error;
      });
  }, [batchUpdate, create, getSourceCompatibility, projects, sourceIdInput]);

  // only on single update and create project
  const errorInputName = React.useMemo(() => {
    if (submitted && !batchUpdate && !nameValue?.length)
      return t("projects.modal.create_or_edit.input.name.error.cannot_be_empty");

    if (!batchUpdate && isLeft(short(io.string).decode(nameValue)))
      return t("projects.modal.create_or_edit.input.name.error.too_long");

    if (
      currentAllProjects.current
        .filter(p => p.id !== projects?.[0]?.id)
        ?.find(p => p.name === nameValue?.trim())
    )
      return t("projects.modal.create_or_edit.input.name.error.name_already_exists");

    return;
  }, [batchUpdate, nameValue, projects, submitted]);

  const errorInputDescription =
    descriptionValue.length > 1000 ? t("commons.input.error.text_to_long") : undefined;

  const dirty = React.useMemo(
    () =>
      (nameValue.length &&
        sourceIdInput.length &&
        (submitted ||
          isInputNameDirty ||
          isDescriptionDirty ||
          isSourceValueDirty ||
          isTimeZoneValueDirty ||
          projectSiteInput.isDirty ||
          projectTypeInput.isDirty)) ||
      (batchUpdate &&
        (isDescriptionDirty ||
          isTimeZoneValueDirty ||
          projectSiteInput.isDirty ||
          projectTypeInput.isDirty)),
    [
      isDescriptionDirty,
      isInputNameDirty,
      isSourceValueDirty,
      isTimeZoneValueDirty,
      batchUpdate,
      nameValue.length,
      projectSiteInput.isDirty,
      projectTypeInput.isDirty,
      sourceIdInput.length,
      submitted,
    ],
  );

  const valid = React.useMemo((): boolean => {
    if (missingSensorTags === null || missingSensorTags.length) return false;
    return errorInputName === undefined && errorInputDescription === undefined;
  }, [errorInputDescription, errorInputName, missingSensorTags]);

  const handleSubmit = React.useCallback(async () => {
    setSubmitted(true);
    if (!valid) return false;

    const siteId = projectSiteInput.selectedProjectSites?.value ?? null;
    const typeId = projectTypeInput.selectedProjectTypes?.value ?? null;
    const description = descriptionValue.trim().length ? descriptionValue.trim() : null;

    if (batchUpdate) {
      if (!projects.length) throw new Error("should not have projects in batchUpdate modal");

      await updateProjects({
        body: {
          ids: projects.map(p => p.id),
          description: isDescriptionDirty ? description : undefined,
          timezone: isTimeZoneValueDirty ? timeZoneValue : undefined,
          siteId: projectSiteInput.isDirty ? siteId : undefined,
          typeId: projectTypeInput.isDirty ? typeId : undefined,
        },
      });
    } else {
      const name = nameValue.trim();

      if (update) {
        const project = projects[0];
        if (!project) throw new Error("should always have a project in update modal");
        if (projects.length > 1)
          throw new Error("should not have multiple projects in update modal");

        await updateProject({
          body: {
            name,
            sourceId: sourceIdInput,
            description,
            timezone: timeZoneValue,
            siteId,
            typeId,
            draft: project.draft,
            showPartLoadSensitiveKpis: project.showPartLoadSensitiveKpis,
            failureColors: project.failureColors,
            manualFailureColors: project.manualFailureColors,
          },
          routeParams: { projectId: project.id },
        });
      } else if (create) {
        if (projects.length) throw new Error("should not have projects in create modal");

        const { body: projectId } = await createProject({
          body: {
            name,
            description,
            sourceId: sourceIdInput,
            draft: true,
            timezone: timeZoneValue,
            siteId,
            typeId,
            failureColors: {},
            manualFailureColors: {},
            hasActivatedDataFlow: false,
            minutesBetweenActiveAnalysis: 1440,
          },
        });
        const { body: project } = await call("GET", "/projects/:projectId(uuid)", {
          routeParams: { projectId },
        });

        // we don't want a transitional 404 page (UX)
        mutateProjects(({ collection, ...rest }) => ({
          collection: [...collection, project],
          ...rest,
        }));

        // go to project page on creation
        void navigate(toPath("/projects/:projectId", { projectId }), {
          replace: true,
        });
      }
    }

    const message = batchUpdate ? (
      t("projects.modal.edit.update_projects.toast", {
        projectsCount: projects.length,
        name: isInputNameDirty,
        description: isDescriptionDirty,
        timeZone: isTimeZoneValueDirty,
      })
    ) : update ? (
      t("projects.modal.edit.update_project.toast", {
        projectName: projects[0]!.name,
        name: isInputNameDirty,
        description: isDescriptionDirty,
        timeZone: isTimeZoneValueDirty,
      })
    ) : create ? (
      <>
        <p>{t("projects.modal.toast.create_project.text", { name: nameValue })}</p>
      </>
    ) : null;

    if (message === null) throw new Error("invalid action");

    addToast("success", message);

    return true;
  }, [
    addToast,
    batchUpdate,
    create,
    createProject,
    descriptionValue,
    isDescriptionDirty,
    isInputNameDirty,
    isTimeZoneValueDirty,
    mutateProjects,
    nameValue,
    navigate,
    projectSiteInput.isDirty,
    projectSiteInput.selectedProjectSites?.value,
    projectTypeInput.isDirty,
    projectTypeInput.selectedProjectTypes?.value,
    projects,
    sourceIdInput,
    timeZoneValue,
    update,
    updateProject,
    updateProjects,
    valid,
  ]);

  const sourcesOptions = React.useMemo(
    () =>
      sources
        .map(src => ({
          value: src.id,
          label: src.id,
        }))
        .sort(comparatorBy(s => s.label)),
    [sources],
  );

  const timeZonesOptions = React.useMemo(() => {
    const timeZones = [...new Set([...browserTimeZones, defaultTimeZone])];

    return timeZones.map(tz => ({ value: tz, label: tz }));
  }, []);

  return (
    <ModalForm
      onSubmit={dirty && valid ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={okButtonName}
    >
      <InputV2
        label={t("projects.modal.update_projects.title.name")}
        required
        placeholder={namePlaceholder}
        autoFocus
        error={errorInputName}
        disabled={batchUpdate}
        value={nameValue}
        dirty={isInputNameDirty}
        onChange={handleNameChanged}
        defaultValue={inputNameDefaultValue}
      />
      <InputV2
        label={t("projects.modal.update_projects.title.description")}
        placeholder={descriptionPlaceholder}
        autoFocus={batchUpdate}
        error={errorInputDescription}
        onChange={handleDescriptionChanged}
        value={descriptionValue}
        dirty={isDescriptionDirty}
        defaultValue={
          !batchUpdate && inputNameDefaultValue
            ? descriptionDefaultValue || ""
            : descriptionDefaultValue
        }
        batch={batchUpdate}
      />
      <Select
        required
        filtered
        onChange={handleTimeZoneChanged}
        value={timeZoneValue}
        options={timeZonesOptions}
        prompt={timeZonePlaceholder}
        revertable={projects.length > 0 && isTimeZoneValueDirty}
        revert={handleRollbackTimeZone}
        clearable={false}
        label={t("projects.modal.update_projects.title.timezone")}
      />
      <Select
        required
        filtered
        disabled={batchUpdate || missingSensorTags === null}
        onChange={handleSourceValueChanged}
        error={
          missingSensorTags && missingSensorTags.length
            ? t("projects.modal.edit.input.dataSource.error", { missingSensorTags })
            : undefined
        }
        value={sourceIdInput}
        options={sourcesOptions}
        tooltip={batchUpdate ? <>{t("projects.modal.edit.input.dataSource.tooltip")}</> : undefined}
        prompt={datasourcePlaceHolder}
        revertable={projects.length > 0 && isSourceValueDirty}
        revert={handleRollbackSources}
        clearable={false}
        label={t("projects.modal.update_projects.title.dataSource")}
      />
      <Title>
        {t("projects.modal.create_or_edit.input.site")}
        {!projectNameInitialyEmpty.current &&
          !projectSiteInitialyEmpty.current &&
          projectSiteInput.isDirty && <Rollback onClick={() => projectSiteInput.resetValue()} />}
      </Title>
      <ReactSelect
        menuPortalTarget={window.document.body}
        placeholder={projectSiteInput.placeholder}
        value={projectSiteInput.selectedProjectSites}
        onChange={projectSiteInput.handleProjectSiteChanged}
        options={projectSiteInput.projectSiteOptions.map(mapValueToOptions)}
        onCreate={useCanEditCategory ? projectSiteInput.handleProjectSiteCreated : undefined}
        onEdit={useCanEditCategory ? projectSiteInput.handleProjectSiteEdited : undefined}
        onDelete={useCanEditCategory ? projectSiteInput.handleProjectSiteDeleted : undefined}
      />
      <Title>
        {t("projects.modal.create_or_edit.input.type", translations)}
        {!projectNameInitialyEmpty.current &&
          !projectTypeInitialyEmpty.current &&
          projectTypeInput.isDirty && <Rollback onClick={() => projectTypeInput.resetValue()} />}
      </Title>
      <ReactSelect
        menuPortalTarget={window.document.body}
        placeholder={projectTypeInput.placeholder}
        value={projectTypeInput.selectedProjectTypes}
        onChange={projectTypeInput.handleProjectTypeChanged}
        options={projectTypeInput.projectTypeOptions.map(mapValueToOptions)}
        onCreate={useCanEditCategory ? projectTypeInput.handleProjectTypeCreated : undefined}
        onEdit={useCanEditCategory ? projectTypeInput.handleProjectTypeEdited : undefined}
        onDelete={useCanEditCategory ? projectTypeInput.handleProjectTypeDeleted : undefined}
      />
    </ModalForm>
  );
};

export default React.memo(CreateUpdateProjectsModalForm);
